/* Rhody Reviews api/v1 -- review-mining domain (Customer Language Report).

   Binds GET /api/v1/review-mining/language-card (engine/api/v1/review_mining.py).
   One LLM pass over the client's stored reviews returns the top verbatim
   benefit phrases customers use + their top objections/worries, each traced to
   the real review it was quoted from. The server validates every phrase is a
   substring of a stored review, so a hallucinated quote never reaches the UI.

   Framed as INTERNAL insight ("language your customers use"), NOT testimonial
   copy -- see card.disclaimer. */
import { request } from './_http';

export interface CustomerLanguagePhrase {
  /* The customer's exact words, copied verbatim from a real review. */
  phrase: string;
  /* The review_id this phrase was drawn from (source traceability). */
  review_id: string;
  reviewer_name: string;
  rating: number | null;
  /* How many distinct reviews express this idea (model-estimated, >= 1). */
  mentions: number;
}

export interface CustomerLanguageHeadline {
  /* A ready-to-use marketing headline, lightly formatted. */
  headline: string;
  /* The verbatim review words the headline is grounded in (validated). */
  source_phrase: string;
  review_id: string;
}

export interface CustomerLanguageCard {
  client_id: string;
  title: string;
  subtitle: string;
  /* Pro-gated feature. When is_pro is false the card is locked. */
  is_pro: boolean;
  locked?: boolean;
  /* The calendar month (YYYY-MM) this report was refreshed for. */
  refreshed_for_month: string;
  reviews_total: number;
  reviews_analyzed: number;
  /* True when fewer than min_reviews reviews-with-text exist: the card still
     shows what's real but flags that more reviews are needed. */
  needs_more_reviews: boolean;
  min_reviews: number;
  benefits: CustomerLanguagePhrase[];
  objections: CustomerLanguagePhrase[];
  headlines: CustomerLanguageHeadline[];
  disclaimer: string;
  /* Present in thin/empty/locked states; an owner-friendly explanation. */
  note?: string;
}

export interface CustomerLanguageCardResponse {
  card: CustomerLanguageCard;
}

/* The reusable analysis contract (POST /review-mining/analyze) -- the SAME
   shape the future free public lead-gen tool consumes. */
export interface ReviewMiningAnalysis {
  review_count: number;
  thin_data: boolean;
  min_reviews: number;
  benefit_phrases: CustomerLanguagePhrase[];
  objections: CustomerLanguagePhrase[];
  headlines: CustomerLanguageHeadline[];
  disclaimer: string;
  /* Present only when analyzed from a Google Place ID/URL. */
  place?: {
    name: string;
    place_id: string;
    google_rating: number | null;
    google_review_count: number;
    reviews_available: number;
  };
}

export interface AnalyzeReviewsRequest {
  reviews?: Array<{
    review_text: string;
    rating?: number;
    reviewer_name?: string;
    review_id?: string;
  }>;
  place_id?: string;
  place_url?: string;
}

export const reviewMiningApi = {
  /* In-product Pro card for the signed-in client. Pro-gated + monthly-refreshed
     server-side. Read-only: no gating, no posting, no SMS. */
  getCustomerLanguageCard(
    signal?: AbortSignal,
  ): Promise<CustomerLanguageCardResponse> {
    return request<CustomerLanguageCardResponse>(
      '/api/v1/review-mining/language-card',
      { signal },
    );
  },

  /* Reusable analysis -- the same engine the Pro card uses. Input = a reviews
     array OR a Google place_id/place_url. The free lead-gen tool calls this. */
  analyzeReviews(
    body: AnalyzeReviewsRequest,
    signal?: AbortSignal,
  ): Promise<ReviewMiningAnalysis> {
    return request<ReviewMiningAnalysis>('/api/v1/review-mining/analyze', {
      method: 'POST',
      body,
      signal,
    });
  },
};
